/**
 * Appointment Service
 * Handles appointment-related data fetching functionality
 */

import { AbstractService } from '@/services/core/serviceFactory';
import { appointmentsGraphqlService, clinicsGraphqlService } from '@/services/api';
import type { GraphQLClientResponse } from '@/services/core/graphqlClient';

export interface Facility {
  facility_id: number;
  facility_name: string;
}

export interface AppointmentDate {
  appointment_date: string;
  fmt_appointment_date: string;
}

export interface AppointmentTime {
  appointment_hour: string;
  fmt_appointment_hour: string;
}

export interface FacilitiesResponse {
  pep_vw_consolidated_data: Facility[];
}

export interface AppointmentDatesResponse {
  pep_vw_available_clinic_appointments: AppointmentDate[];
}

export interface AppointmentTimesResponse {
  pep_vw_available_clinic_appointments: AppointmentTime[];
}

export class AppointmentService extends AbstractService {
  /**
   * Fetch facilities for a specific clinic
   */
  async fetchFacilities(clinicId: number): Promise<GraphQLClientResponse<FacilitiesResponse>> {
    this.requireAuth();

    try {
      const facilities = await appointmentsGraphqlService.getAppointmentFacilities(clinicId, this.context.hasuraToken!);
      return {
        data: { pep_vw_consolidated_data: facilities },
        errors: null,
        success: true,
        status: 200
      } as GraphQLClientResponse<FacilitiesResponse>;
    } catch (error) {
      console.error('Error fetching facilities:', error);
      return {
        data: { pep_vw_consolidated_data: [] },
        errors: ['Failed to fetch facilities'],
        success: false,
        status: 500
      } as GraphQLClientResponse<FacilitiesResponse>;
    }
  }

  /**
   * Fetch available appointment dates for a facility
   */
  async fetchAppointmentDates(facilityId: number): Promise<GraphQLClientResponse<AppointmentDatesResponse>> {
    this.requireAuth();

    try {
      const dates = await appointmentsGraphqlService.getAppointmentDates(facilityId, this.context.hasuraToken!);
      return {
        data: { pep_vw_available_clinic_appointments: dates },
        errors: null,
        success: true,
        status: 200
      } as GraphQLClientResponse<AppointmentDatesResponse>;
    } catch (error) {
      console.error('Error fetching appointment dates:', error);
      return {
        data: { pep_vw_available_clinic_appointments: [] },
        errors: ['Failed to fetch appointment dates'],
        success: false,
        status: 500
      } as GraphQLClientResponse<AppointmentDatesResponse>;
    }
  }

  /**
   * Fetch available appointment times for a facility and date
   */
  async fetchAppointmentTimes(facilityId: number, appointmentDate: string): Promise<{ success: boolean; data?: AppointmentTimesResponse; errors?: string[] }> {
    this.requireAuth();

    try {
      const times = await appointmentsGraphqlService.getAppointmentTimes(facilityId, appointmentDate, this.context.hasuraToken!);
      return {
        success: true,
        data: { pep_vw_available_clinic_appointments: times }
      };
    } catch (error) {
      console.error('Error fetching appointment times:', error);
      return {
        success: false,
        errors: ['Failed to fetch appointment times']
      };
    }
  }

  /**
   * Fetch appointment details for an activity (for editing)
   */
  async fetchActivityAppointmentDetails(activityId: number): Promise<{ success: boolean; data?: any[]; errors?: string[] }> {
    this.requireAuth();

    try {
      const details = await appointmentsGraphqlService.getActivityAppointmentDetails(activityId, this.context.hasuraToken!);
      return {
        success: true,
        data: details
      };
    } catch (error) {
      console.error('Error fetching activity appointment details:', error);
      return {
        success: false,
        errors: ['Failed to fetch appointment details']
      };
    }
  }
}

// Export singleton instance
export const appointmentService = new AppointmentService();